import type {IconName} from '@lightricks/react-design-system';
import translate from '@/utils/translate';

/**
 * Maps paidGig deliverable kind values (as sent to/from the API) to display labels.
 * Used by NegotiateModal (to build the payload) and NegotiationSnapshotDisplay (to render kind names).
 */
export const DELIVERABLE_KINDS = {
  requiredFeedPostsCount: 'photos_to_post',
  requiredStoryPostsCount: 'stories_to_post',
  requiredReelsPostsCount: 'reels_to_post',
  photosToUpload: 'photos_to_upload',
  videosToUpload: 'videos_to_upload',
  mixedMediaToUpload: 'mixed_media_to_upload',
  blogsToUpload: 'blogs_to_upload',
  minimumNumberOfMixedMedia: 'minimum_number_of_mixed_media',
} as const;

export type DeliverableKindValue =
  (typeof DELIVERABLE_KINDS)[keyof typeof DELIVERABLE_KINDS];

export const DELIVERABLE_ICON_NAME: Record<string, IconName> = {
  story_post: 'Social-InstagramStories',
  feed_post_photo: 'Social-Instagram',
  reel_post: 'Social-InstagramReels',
  tiktok_video: 'Social-TikTok',
  content_standard_photo: 'Navigation-Photos-Light',
  content_standard_video: 'Social-InstagramReels',
  content_premium_photo: 'Navigation-Photos-Light',
  content_premium_video: 'Social-InstagramReels',
  youtube_short_form_video: 'Social-YouTube',
  youtube_long_form_video: 'Social-YouTube',
  youtube_short: 'Social-YTShorts',
};

export const DELIVERABLE_KIND_LABEL: Record<string, string> = {
  [DELIVERABLE_KINDS.requiredFeedPostsCount]: 'Feed Posts',
  [DELIVERABLE_KINDS.requiredStoryPostsCount]: 'Story Posts',
  [DELIVERABLE_KINDS.requiredReelsPostsCount]: 'Reels',
  [DELIVERABLE_KINDS.photosToUpload]: 'Photos',
  [DELIVERABLE_KINDS.videosToUpload]: 'Videos',
  [DELIVERABLE_KINDS.mixedMediaToUpload]: 'Story Uploads',
  [DELIVERABLE_KINDS.blogsToUpload]: 'Blogs',
  [DELIVERABLE_KINDS.minimumNumberOfMixedMedia]: 'Min # of Story Frames',
};

// Mirrors useCampaignQuery's campaignNetworkPrettyName() — kept here so snapshot
// display components can derive a pretty name without pulling in the campaign query.
export function getCampaignNetworkPrettyName(
  network: string | null | undefined
): string | null {
  if (!network) {
    return null;
  }
  switch (network) {
    case 'youtube':
      return 'YouTube';
    // tiktok and tiktok_v2 both surface as "TikTok" to the user
    case 'tiktok':
    case 'tiktok_v2':
      return 'TikTok';
    case 'tiktok_creator_marketplace':
      return 'TikTok Creator Marketplace';
    default:
      return network.charAt(0).toUpperCase() + network.slice(1);
  }
}

// photos_to_post is network-aware: Ember renders it as "{network} Posts"
// (e.g. "TikTok Posts" for TikTok campaigns, "Instagram Posts" for Instagram).
// All other kinds use a static label regardless of network.
export function getDeliverableKindLabel(
  kind: string,
  networkPrettyName: string | null | undefined
): string {
  if (kind === DELIVERABLE_KINDS.requiredFeedPostsCount && networkPrettyName) {
    return translate('network-posts', {network: networkPrettyName});
  }
  return translate(DELIVERABLE_KIND_LABEL[kind] ?? kind);
}
